import { useContext, useEffect, useState } from "react";
import SupplierContext from "../../../../contexts/SupplierContext";
import SortOrderEnum from "../../../../models/SortOrderEnum";
import * as _ from "lodash-es";
import moment from "moment-timezone";
import IOrder_Item from "../../../../models/IOrder_Item";
import PackingListItemRow from "../../../shipments/ShipmentPackingList/PackingListItemRow/PackingListItemRow";
import UIService from "../../../../services/UIService";
import { DocLogo } from "../../DocLogo/DocLogo";
import { WhiteLabelLogo } from "../../WhiteLabelLogo/WhiteLabelLogo";
import OwTitle from "../../OwTitle/OwTitle";
import OwTranslate from "../../OwTranslate/OwTranslate";
import OwLabel from "../../OwLabel/OwLabel";
import SupplierGuidEnum from "../../../../models/SupplierGuidEnum";
import AddressDisplay from "../../AddressDisplay/AddressDisplay";
import OwSortHeader from "../../OwSortHeader/OwSortHeader";
import IOrder from "../../../../models/IOrder";
import IPackingListViewOptions from "../../../../models/IPackingListViewOptions";
import { SimplePackingListItemRow } from "../SimplePackingListItemRow/SimplePackingListItemRow";
import UserService from "@services/UserService";

type Props = {
    order: IOrder;
    sortCol: "item_no" | "sku" | "location";
    template: "STANDARD" | "MINIMAL";
    viewOptions: IPackingListViewOptions;
};

export const SimplePackingListDetail = (props: Props) => {
    const supplierModel = useContext(SupplierContext);
    const vendorModel = props.order.Vendor;
    const supplierShipTo = supplierModel!.Ship_To;

    const [sortCol, setSortCol] = useState<string>(props.sortCol as string);
    const [sortDir, setSortDir] = useState(SortOrderEnum.ASC);
    const [thHeightFix, setThHeightFix] = useState<boolean>(props.viewOptions.showItemLocation);

    useEffect(() => {
        setThHeightFix(props.viewOptions.showItemLocation);
    }, [props.viewOptions.showItemLocation]);

    const updateSortFn = (_sortCol: string, _sortDir: SortOrderEnum) => {
        setSortCol(_sortCol);
        setSortDir(_sortDir);
    };

    const shipmentGroupShipTo = !_.isNil(props.order.Ship_To) ? props.order.Ship_To : vendorModel?.Ship_To;
    const shipmentGroupBillTo = vendorModel?.Bill_To;

    const OrderItemsParsed = _.map(props.order.Order_Items, (item) => {
        item.item_no = Number(item.item_no);
        return item;
    });

    const OrderItems = _.sortBy(OrderItemsParsed, ["item_no", "asc"]);
    // const OrderItems = _.sortBy(props.order.Order_Items, ["item_no", "asc"]);
    const orderModel = props.order;

    const orderDate =
        orderModel.confirmed_date || orderModel.submit_date
            ? UserService.parseDate(orderModel.confirmed_date || orderModel.submit_date)
            : "";

    const estShipDate = UserService.parseDate(orderModel.estimated_shipping_at);

    const cancelDate = UserService.parseDate(orderModel.cancel_date);
    const shippingDate = UserService.parseDate(orderModel.ship_date);
    const doNotShipBeforeDate = UserService.parseDate(orderModel.do_not_ship_before_date);

    const orderNumsArr: string[] = [];
    const poNumsArr: string[] = [];
    const notes = orderModel.note || "";

    orderNumsArr.push(orderModel?.name || "");

    if (orderModel.po_no) {
        poNumsArr.push(orderModel.po_no);
    }

    const orderNums = orderNumsArr.join(",");
    const poNums = poNumsArr.join(",");

    let totalQty = 0;

    const sortOrderItemsFn = (itemA: IOrder_Item, itemB: IOrder_Item): number => {
        if (sortCol === "location") {
            return sortOrderLocationFn(itemA, itemB);
        } else {
            return sortOrderItemNoFn(itemA, itemB);
        }
    };

    const sortOrderItemNoFn = (itemA: IOrder_Item, itemB: IOrder_Item): number => {
        if (itemA.item_no < itemB.item_no) {
            return -1;
        } else if (itemA.item_no > itemB.item_no) {
            return 1;
        } else {
            return 0;
        }
    };

    const sortOrderLocationFn = (itemA: IOrder_Item, itemB: IOrder_Item): number => {
        if ((itemA.Catalog_Item!.row || "") < (itemB.Catalog_Item!.row || "")) {
            return -1;
        } else if ((itemA.Catalog_Item!.row || "") > (itemB.Catalog_Item!.row || "")) {
            return 1;
        } else {
            return 0;
        }
    };

    const orderItemRows = OrderItems.sort(sortOrderItemsFn).map((orderItem: any, index: number) => {
        totalQty += Number(orderItem.qty);

        return (
            <SimplePackingListItemRow
                key={orderItem.guid}
                order={props.order}
                orderItem={orderItem}
                viewOptions={props.viewOptions}
                supplierModel={supplierModel}
            />
        );
    });

    let tableTheme = "";

    if (!UIService.isMainViewLight()) {
        tableTheme = "table-dark";
    }

    let hasBillToName = true;
    let hasShipToName = true;

    if (!shipmentGroupBillTo?.name?.length || !shipmentGroupBillTo?.company_name?.length) {
        let billToNameSame = false;
        let billToCompanyNameSame = false;

        if (shipmentGroupBillTo?.name === vendorModel?.name) {
            billToNameSame = true;
        }

        if (shipmentGroupBillTo?.company_name === vendorModel?.name) {
            billToCompanyNameSame = true;
        }

        if (!billToNameSame && !billToCompanyNameSame) {
            hasBillToName = false;
        }
    }

    if (!shipmentGroupShipTo?.name?.length || !shipmentGroupShipTo?.company_name?.length) {
        let shipToNameSame = false;
        let shipToCompanyNameSame = false;

        if (shipmentGroupShipTo?.name === vendorModel?.name) {
            shipToNameSame = true;
        }

        if (shipmentGroupShipTo?.company_name === vendorModel?.name) {
            shipToCompanyNameSame = true;
        }

        if (!shipToNameSame && !shipToCompanyNameSame) {
            hasShipToName = false;
        }

        if (props.order.client_id) {
            hasShipToName = true;
        }
    }

    const User = props.order.User!;

    const getShowBillTo = () => {
        if (props.viewOptions.showBillTo) {
            if (props.template === "STANDARD") {
                return true;
            }
        }

        return false;
    };

    const showDetailRow = () => {
        return props.template === "STANDARD";
    };

    return (
        <div className={"OrderPackingListView " + props.template}>
            <div className={"top-row"}>
                <div className={"d-flex justify-content-between"}>
                    <div className={" p-1 " + (props.viewOptions.inlineLogoName ? "d-flex" : "")}>
                        {supplierModel.Doc_Logo ? (
                            <DocLogo maxWidth={props.template === "STANDARD" ? "20rem" : "10rem"} maxHeight={"8rem"} height={"auto"} />
                        ) : (
                            <WhiteLabelLogo maxWidth={"20rem"} />
                        )}
                        {props.viewOptions.hideBusinessName ? null : (
                            <div className={props.viewOptions.inlineLogoName ? "ms-4" : ""}>
                                <OwTitle disableTranslation={true} title={supplierModel.name} tag={"h5"} className={"mb-0"} />
                                <div>{supplierShipTo.address_line_one}</div>
                                <div>{supplierShipTo.address_line_two}</div>
                                <div>
                                    {supplierShipTo.city}
                                    {supplierShipTo.state?.length ? "," : ""} {supplierShipTo.state} {supplierShipTo.postal_code}
                                </div>
                                <div className={"mt-2"}>
                                    {supplierModel.has_user_detail_on_packing_list ? (
                                        <div>
                                            <b>{User.name}</b>
                                        </div>
                                    ) : null}
                                    <div className={props.viewOptions.showPhone && supplierModel.phone ? "" : "d-none"}>
                                        <b>
                                            <OwTranslate defaultMessage={"phone"} id={"phone"} />:
                                        </b>{" "}
                                        {supplierModel.has_user_detail_on_packing_list ? User.phone || "" : supplierModel.phone}
                                    </div>
                                    <div className={props.viewOptions.showEmail && supplierModel.email ? "" : "d-none"}>
                                        <b>
                                            <OwTranslate defaultMessage={"email"} id={"email"} />:
                                        </b>{" "}
                                        {supplierModel.has_user_detail_on_packing_list ? User.email || "" : supplierModel.email}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className={"text-end align-items-end d-md-flex flex-column"}>
                        <OwTitle title={"packing_list"} tag={"h3"} isBold={true} />
                        <div>
                            <OwLabel title={"date"} postText={":"} /> {moment().format("MM/DD/YYYY")}
                        </div>
                        {props.order.allow_substitutions ? (
                            <p className={"mt-2"}>
                                <OwLabel title={"allow_substitutions"} />
                            </p>
                        ) : null}
                        {props.order.allow_backorders ? (
                            <p className={"mt-2"}>
                                <OwLabel title={"allow_backorders"} />
                            </p>
                        ) : null}
                        {props.order.is_local_pickup ? (
                            <p className={"mt-4"}>
                                <OwTitle title={"local_pickup"} tag={"h4"} />
                            </p>
                        ) : null}
                        {props.order.is_local_delivery ? (
                            <p className={"mt-4"}>
                                <OwTitle title={"local_delivery"} tag={"h4"} />
                            </p>
                        ) : null}
                    </div>
                </div>
            </div>

            <div className={"row bill-to-ship-to-row"}>
                {getShowBillTo() ? (
                    <div
                        className={
                            (props.template === "STANDARD" ? "w-20r" : "") +
                            " col-auto " +
                            (supplierModel.guid === SupplierGuidEnum.VERMONT ? "d-none" : "")
                        }>
                        <div className={"black-box p-2 h-100"}>
                            <OwTitle leftIconName={"fal fa-building"} className={"underline"} title={"bill_to"} tag={"h5"} />
                            {!hasBillToName ? (
                                <OwTitle title={props.order.Vendor?.name || ""} tag={"h5"} disableTranslation={true} />
                            ) : null}
                            <AddressDisplay
                                address={shipmentGroupBillTo}
                                showCompanyName={true}
                                showName={true}
                                showPhone={props.viewOptions.showBillToPhone}
                                hideMobile={true}
                                hideFax={true}
                                hideEmail={true}
                                nameTag={"h5"}
                            />
                        </div>
                    </div>
                ) : null}

                <div
                    className={
                        (props.template === "STANDARD" ? "w-20r" : "") +
                        " col-auto " +
                        (supplierModel.guid === SupplierGuidEnum.VERMONT ? "" : "border-left-0")
                    }>
                    <div className={"black-box p-2 h-100 "}>
                        {props.order.is_local_pickup ? (
                            <OwTitle leftIconName={"fal fa-hand-holding-box"} title={"local_pickup"} tag={"h5"} className={"underline"} />
                        ) : (
                            <OwTitle
                                leftIconName={"fal fa-truck"}
                                title={props.order.is_local_delivery ? "deliver_to" : "ship_to"}
                                tag={"h5"}
                                className={"underline"}
                            />
                        )}
                        {!hasShipToName ? <OwTitle title={props.order.Vendor?.name || ""} tag={"h5"} disableTranslation={true} /> : null}
                        <AddressDisplay
                            address={shipmentGroupShipTo}
                            showCompanyName={!props.order.client_id}
                            showName={true}
                            showPhone={props.viewOptions.showShipToPhone}
                            hideEmail={true}
                            hideFax={true}
                            hideMobile={true}
                            nameTag={"h5"}
                        />
                    </div>
                </div>

                {props.template === "MINIMAL" ? (
                    <div className={"col-auto"}>
                        <table className={"table table-sm table-bordered w-100 " + tableTheme}>
                            <tbody>
                                <tr>
                                    <th>
                                        <strong>
                                            <OwLabel title={"order_date"} className={"mb-0"} />
                                        </strong>
                                    </th>
                                    <td>{orderDate}</td>
                                </tr>
                                {supplierModel?.shipping_lead_time ? (
                                    <tr>
                                        <th>
                                            <strong>
                                                <OwLabel title={"est_ship_date"} className={"mb-0"} />
                                            </strong>
                                        </th>
                                        <td>{estShipDate}</td>
                                    </tr>
                                ) : null}
                                {supplierModel?.has_order_cancel_date ? (
                                    <tr>
                                        <th>
                                            <strong>
                                                <OwLabel title={"cancel_date"} className={"mb-0"} />
                                            </strong>
                                        </th>
                                        <td>{props.order.cancel_date ? cancelDate : null}</td>
                                    </tr>
                                ) : null}
                                {!supplierModel?.has_est_ship_date ? (
                                    <tr>
                                        <th>
                                            <strong>
                                                <OwLabel title={"ship_date"} className={"mb-0"} />
                                            </strong>
                                        </th>
                                        <td>{props.order.ship_date ? shippingDate : null}</td>
                                    </tr>
                                ) : null}
                                {supplierModel?.has_do_not_ship_before_date && props.viewOptions.showDoNotShipBeforeDate ? (
                                    <tr>
                                        <th>
                                            <strong>
                                                <OwLabel title={"do_not_ship_before_date"} className={"mb-0"} />
                                            </strong>
                                        </th>
                                        <td>{props.order.do_not_ship_before_date ? doNotShipBeforeDate : null}</td>
                                    </tr>
                                ) : null}
                                <tr>
                                    <th>
                                        <strong>
                                            <OwLabel title={"order_no"} className={"mb-0"} />
                                        </strong>
                                    </th>
                                    <td>{orderNums}</td>
                                </tr>
                                {supplierModel?.has_terms_on_packing_list ? (
                                    <tr>
                                        <th>
                                            <strong>
                                                <OwLabel title={"terms"} className={"mb-0"} />
                                            </strong>
                                        </th>
                                        <td>
                                            <OwLabel title={props.order.Vendor?.Payment_Type?.name.toLowerCase()} noBottomMargin={true} />
                                        </td>
                                    </tr>
                                ) : null}
                                <tr>
                                    <th>
                                        <strong>
                                            <OwLabel title={"po_no"} className={"mb-0"} />
                                        </strong>
                                    </th>
                                    <td>{poNums}</td>
                                </tr>
                                <tr>
                                    <th>
                                        <strong>
                                            <OwLabel title={"notes"} className={"mb-0"} />
                                        </strong>
                                    </th>
                                    <td>{notes}</td>
                                </tr>
                                {props.order.shipping_instructions ? (
                                    <tr>
                                        <th>
                                            <strong>
                                                <OwLabel title={"shipping_instructions"} className={"mb-0"} />
                                            </strong>
                                        </th>
                                        <td>{props.order.shipping_instructions}</td>
                                    </tr>
                                ) : null}
                                {props.order.description ? (
                                    <tr>
                                        <th>
                                            <strong>
                                                <OwLabel title={"description"} />
                                            </strong>
                                        </th>
                                        <td>{props.order.description}</td>
                                    </tr>
                                ) : null}
                                {props.viewOptions.showOrderUser ? (
                                    <tr>
                                        <th>
                                            <strong>
                                                <OwLabel title={"user"} />
                                            </strong>
                                        </th>
                                        <td>{props.order.User?.name}</td>
                                    </tr>
                                ) : null}
                            </tbody>
                        </table>
                    </div>
                ) : null}
            </div>

            {showDetailRow() ? (
                <div className={"details-row"}>
                    <table className={"table table-bordered " + tableTheme}>
                        <tbody>
                            <tr>
                                <th>
                                    <OwLabel title={"order_date"} className={"mb-0"} />
                                </th>
                                {supplierModel?.shipping_lead_time ? (
                                    <th>
                                        <OwLabel title={"est_ship_date"} className={"mb-0"} />
                                    </th>
                                ) : null}
                                {supplierModel?.has_order_cancel_date ? (
                                    <th>
                                        <OwLabel title={"cancel_date"} className={"mb-0"} />
                                    </th>
                                ) : null}
                                {!supplierModel?.has_est_ship_date ? (
                                    <th>
                                        <OwLabel title={"ship_date"} className={"mb-0"} />
                                    </th>
                                ) : null}
                                {supplierModel?.has_do_not_ship_before_date && props.viewOptions.showDoNotShipBeforeDate ? (
                                    <th>
                                        <OwLabel title={"do_not_ship_before_date"} className={"mb-0"} />
                                    </th>
                                ) : null}
                                <th>
                                    <OwLabel title={"order_no"} className={"mb-0"} />
                                </th>
                                {supplierModel?.has_terms_on_packing_list ? (
                                    <th>
                                        <OwLabel title={"terms"} className={"mb-0"} />
                                    </th>
                                ) : null}
                                <th>
                                    <OwLabel title={"po_no"} className={"mb-0"} />
                                </th>
                                <th>
                                    <OwLabel title={"notes"} className={"mb-0"} />
                                </th>
                                {props.order.shipping_instructions ? (
                                    <th>
                                        <OwLabel title={"shipping_instructions"} className={"mb-0"} />
                                    </th>
                                ) : null}
                                {props.order.description ? (
                                    <th>
                                        <OwLabel title={"description"} />
                                    </th>
                                ) : null}
                                {props.viewOptions.showOrderUser ? (
                                    <th>
                                        <OwLabel title={"user"} />
                                    </th>
                                ) : null}
                            </tr>
                            <tr>
                                <td>{orderDate}</td>
                                {supplierModel?.shipping_lead_time ? <td>{estShipDate}</td> : null}
                                {supplierModel?.has_order_cancel_date ? <td>{props.order.cancel_date ? cancelDate : null}</td> : null}
                                {!supplierModel?.has_est_ship_date ? <td>{props.order.ship_date ? shippingDate : null}</td> : null}
                                {supplierModel?.has_do_not_ship_before_date && props.viewOptions.showDoNotShipBeforeDate ? (
                                    <td>{props.order.do_not_ship_before_date ? doNotShipBeforeDate : null}</td>
                                ) : null}
                                <td>{orderNums}</td>
                                {supplierModel?.has_terms_on_packing_list ? (
                                    <td>
                                        <OwLabel title={props.order.Vendor?.Payment_Type?.name.toLowerCase()} noBottomMargin={true} />
                                    </td>
                                ) : null}
                                <td>{poNums}</td>
                                <td>{notes}</td>
                                {props.order.shipping_instructions ? <td>{props.order.shipping_instructions}</td> : null}
                                {props.order.description ? <td>{props.order.description}</td> : null}
                                {props.viewOptions.showOrderUser ? <td>{props.order.User?.name}</td> : null}
                            </tr>
                        </tbody>
                    </table>
                </div>
            ) : null}

            <div className={"items-row"}>
                <table className={"table table-bordered " + tableTheme}>
                    <tbody>
                        <tr>
                            {props.viewOptions.showItemNo ? (
                                <th className={thHeightFix ? "align-middle" : ""}>
                                    <OwLabel title={"no"} className={"mb-0 "} />
                                </th>
                            ) : null}
                            {props.viewOptions.showItemLocation ? (
                                <th className={thHeightFix ? "align-middle" : ""}>
                                    {/*<OwLabel title={"location"} className={"mb-0"} />*/}
                                    <OwSortHeader
                                        arrowClassName={"d-print-none"}
                                        title={"location"}
                                        currentSortCol={sortCol}
                                        currentSortOrder={sortDir}
                                        sortFn={updateSortFn}
                                    />
                                </th>
                            ) : null}
                            {props.viewOptions.showItemName ? (
                                <th className={thHeightFix ? "align-middle" : ""}>
                                    <OwLabel title={"item"} className={"mb-0 "} />
                                </th>
                            ) : null}
                            {props.viewOptions.showSku ? (
                                <th className={thHeightFix ? "align-middle" : ""}>
                                    <OwLabel title={"sku"} className={"mb-0 "} />
                                </th>
                            ) : null}
                            {props.viewOptions.showUom ? (
                                <th className={thHeightFix ? "align-middle" : ""}>
                                    <OwLabel title={"uom"} />
                                </th>
                            ) : null}
                            {props.viewOptions.showDimensions ? (
                                <>
                                    <th className={thHeightFix ? "align-middle" : ""}>
                                        <OwLabel title={"W"} />
                                    </th>
                                    <th className={thHeightFix ? "align-middle" : ""}>
                                        <OwLabel title={"H"} />
                                    </th>
                                    <th className={thHeightFix ? "align-middle" : ""}>
                                        <OwLabel title={supplierModel.length_for_depth ? "L" : "D"} />
                                    </th>
                                </>
                            ) : null}
                            {props.viewOptions.showItemOptions ? (
                                <th className={thHeightFix ? "align-middle" : ""}>
                                    <OwLabel title={"item_options"} className={"mb-0 "} />
                                </th>
                            ) : null}
                            <th className={thHeightFix ? "align-middle" : ""}>
                                <OwLabel title={"description"} className={"mb-0 "} />
                            </th>
                            {supplierModel.has_lots ? (
                                <th className={thHeightFix ? "align-middle" : ""}>
                                    <OwLabel title={"lot"} className={"mb-0 " + (thHeightFix ? "mt-2" : "")} />
                                </th>
                            ) : null}
                            <th className={(props.viewOptions.showOrderQty ? "" : "d-none ") + (thHeightFix ? "align-middle" : "")}>
                                <OwLabel title={"order_qty"} className={"mb-0 " + (thHeightFix ? "mt-2" : "")} />
                            </th>
                            <th className={(props.viewOptions.showShipQty ? "" : "d-none ") + (thHeightFix ? "align-middle" : "")}>
                                <OwLabel title={"ship_qty"} className={"mb-0 "} />
                            </th>
                            {props.viewOptions.showUnitPrice ? (
                                <th
                                    className={
                                        "text-end " +
                                        (props.viewOptions.showUnitPrice ? "" : "d-none ") +
                                        (thHeightFix ? "align-middle" : "")
                                    }>
                                    <OwLabel title={"unit_price"} className={"mb-0 "} />
                                </th>
                            ) : null}
                        </tr>
                        {orderItemRows}
                    </tbody>
                    <tbody>
                        <tr>
                            {props.viewOptions.showItemNo ? <td></td> : null}
                            {props.viewOptions.showItemLocation ? <td></td> : null}
                            {props.viewOptions.showItemName ? <td></td> : null}
                            {props.viewOptions.showSku ? <td></td> : null}
                            {props.viewOptions.showUom ? <td></td> : null}
                            {props.viewOptions.showDimensions ? <td></td> : null}
                            {props.viewOptions.showDimensions ? <td></td> : null}
                            {props.viewOptions.showDimensions ? <td></td> : null}
                            {props.viewOptions.showItemOptions ? <td></td> : null}
                            <td></td>
                            {supplierModel.has_lots ? <td></td> : null}
                            <td className={"w-15 " + (props.viewOptions.showOrderQty ? "" : "d-none")}>{totalQty}</td>
                            <td className={"w-15 " + (props.viewOptions.showShipQty ? "" : "d-none")}></td>
                            {props.viewOptions.showUnitPrice ? (
                                <td className={"w-15 " + (props.viewOptions.showUnitPrice ? "" : "d-none")}></td>
                            ) : null}
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
};
